import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, BookOpen, Link2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { POSTS } from "@/data/posts";
import { essayUrl } from "@/components/EssayPage";

const wordCount = (post) => post.body.join(" ").split(/\s+/).length;

const reveal = {
  hidden: { opacity: 0, y: 36 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: Math.min(i * 0.06, 0.5), ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Journal = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState("All");
  const tags = ["All", ...new Set(POSTS.map((p) => p.tag))];
  const [featured, ...rest] = POSTS;
  const visible = filter === "All" ? rest : POSTS.filter((p) => p.tag === filter);
  const open = (id) => navigate(`/journal/${id}`);

  return (
    <section id="journal" className="py-24 sm:py-32 lg:py-40" data-testid="journal-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20">
        <motion.p variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} custom={0} className="font-mono text-xs uppercase tracking-[0.24em] text-amber mb-6">
          The Draft — notes from the desk
        </motion.p>
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <motion.h2 variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} custom={1} className="font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-[1.15] max-w-2xl">
            Essays on tech, words, and <em className="text-wine">the work between them.</em>
          </motion.h2>
          <motion.p variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} custom={2} className="font-mono text-[10px] uppercase tracking-[0.2em] text-subtle" data-testid="journal-count">
            {POSTS.length} essays &amp; field notes — each with its own link
          </motion.p>
        </div>

        <motion.div variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} custom={2} className="mt-10 flex flex-wrap gap-2" data-testid="journal-filters">
          {tags.map((t) => (
            <button
              key={t}
              onClick={() => setFilter(t)}
              data-testid={`journal-filter-${t.toLowerCase().replace(/\s+/g, "-")}`}
              className={`px-4 py-2 rounded-full font-mono text-[10px] uppercase tracking-[0.16em] border transition-colors duration-300 ${
                filter === t
                  ? "bg-wine text-paper border-wine"
                  : "border-ink/20 text-subtle hover:border-wine hover:text-wine"
              }`}
            >
              {t}
            </button>
          ))}
        </motion.div>

        {filter === "All" && (
          <motion.article
            variants={reveal}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-60px" }}
            custom={1}
            className="group mt-14 bg-ink text-paper p-8 sm:p-12 grid lg:grid-cols-2 gap-8 items-center hover:shadow-[0_32px_64px_-28px_rgba(26,24,21,0.6)] transition-shadow duration-500 cursor-pointer"
            onClick={() => open(featured.id)}
            data-testid={`journal-card-${featured.id}`}
          >
            <div>
              <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.2em] text-paper/50">
                <span className="text-amber">Featured</span>
                <span>/</span>
                <span>{featured.tag}</span>
                <span>/</span>
                <span>{featured.date}</span>
              </div>
              <h3 className="mt-5 font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-[1.15]">
                {featured.title}
              </h3>
              {featured.image && (
                <p className="mt-5 font-serif italic text-lg text-paper/70 leading-snug max-w-xl">
                  {featured.excerpt}
                </p>
              )}
              <span className="mt-8 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-amber">
                <BookOpen size={14} />
                Read the essay
                <ArrowUpRight size={13} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </span>
            </div>
            {featured.image ? (
              <div className="relative overflow-hidden border border-paper/15">
                <img
                  src={featured.image}
                  alt={`${featured.title} — article illustration`}
                  className="w-full h-64 sm:h-80 object-cover object-[50%_30%] transition-transform duration-700 group-hover:scale-[1.03]"
                />
              </div>
            ) : (
              <p className="font-serif italic text-lg sm:text-xl text-paper/70 leading-snug lg:border-l lg:border-paper/15 lg:pl-10">
                {featured.excerpt}
              </p>
            )}
          </motion.article>
        )}

        <div className={`${filter === "All" ? "mt-6" : "mt-10"} grid sm:grid-cols-2 lg:grid-cols-3 gap-6`} data-testid="journal-grid">
          {visible.map((p, i) => (
            <JournalCard key={p.id} post={p} index={i + 2} onOpen={() => open(p.id)} />
          ))}
        </div>
      </div>
    </section>
  );
};

const JournalCard = ({ post, index, onOpen }) => (
  <motion.article
    variants={reveal}
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, margin: "-60px" }}
    custom={index}
    className="group bg-cream border border-line flex flex-col hover:-translate-y-1 hover:shadow-[0_24px_48px_-24px_rgba(26,24,21,0.35)] transition-[transform,box-shadow] duration-500 cursor-pointer overflow-hidden"
    onClick={onOpen}
    data-testid={`journal-card-${post.id}`}
  >
    {post.image && (
      <img
        src={post.image}
        alt={`${post.title} — article illustration`}
        className="w-full h-44 object-cover object-[50%_20%] border-b border-line"
      />
    )}
    <div className="p-7 flex flex-col flex-1">
      <div className="flex items-center justify-between mb-5 font-mono text-[10px] uppercase tracking-[0.18em]">
        <span className="text-wine">{post.tag}</span>
        <span className="text-subtle">{wordCount(post)} words</span>
      </div>
      <h3 className="font-serif text-xl sm:text-2xl leading-[1.25] tracking-tight">{post.title}</h3>
      <p className="mt-3 text-sm leading-relaxed text-subtle line-clamp-3 flex-1">{post.excerpt}</p>
      <div className="mt-6 flex items-center justify-between">
        <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-subtle">{post.date}</span>
        <span className="flex items-center gap-3">
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigator.clipboard
                .writeText(essayUrl(post.id))
                .then(() => toast.success("Essay link copied."))
                .catch(() => toast.error("Could not copy the link."));
            }}
            className="p-1.5 text-subtle hover:text-wine transition-colors duration-300"
            data-testid={`share-essay-${post.id}`}
            aria-label="Copy essay link"
          >
            <Link2 size={14} />
          </button>
          <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-ink group-hover:text-wine transition-colors duration-300">
            Read <ArrowUpRight size={12} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </span>
        </span>
      </div>
    </div>
  </motion.article>
);
